import React from 'react';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  LOAD_ASSIGNED_ALARM_DEVICE_DETAILS,
  PROCESS_ASSIGNED_ALARM_DEVICE_DETAILS,
  LOAD_ALARM_DEVICE_DETAILS,
  PROCESS_ALARM_DEVICE_DETAILS,
  addAlarmDeviceDetails,
  LOAD_UNASSIGNED_ALARM_DEVICE_DETAILS,
  PROCESS_UNASSIGNED_ALARM_DEVICE_DETAILS,
  LOAD_ALARM_DEVICE_TYPES,
  PROCESS_ALARM_DEVICE_TYPES,
  addAlarmDeviceTypes,
  LOAD_UNASSIGNED_POI_DEVICE_DETAILS,
  LOAD_SPECIFIC_ALARM_DEVICE_DETAILS,
  PROCESS_SPECIFIC_ALARM_DEVICE_DETAILS,
  addSpecificAlarmDeviceDetails,
} from '../actions/alarmDeviceActions';
import { DEVICE_ERROR } from '../actions/deviceActions';
import { FormattedMessage } from 'react-intl';
import { errorDisplay } from '../../utilities/general';
import { messageTypes } from '../../components/ui/AlertMessage';
import { updateApiSpinner } from '../actions/uiActions';

/**
 * Load alarm device details for all monitored devices and assigned details
 *
 * The "monitored=true" is a filter on the API used to filter out a variety of
 * devices and subdevices
 */
export const loadAssignedAlarmDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === LOAD_ASSIGNED_ALARM_DEVICE_DETAILS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alarmdevice +
        '?monitored=true&assigned=true',
      onSuccess: PROCESS_ASSIGNED_ALARM_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ associatedAlarmDevicesReturned: false }));
  }
};

/**
 * Process the alarm device details
 *
 * First check to see if there are additional pages of results and trigger api calls for those,
 * then when all pages are loaded tell the loader bar that loading is done, tell the spinner
 * that everything has been returned, map through the results to get rid of a lot of the unused
 * information to use less space in storage, and then dispatch the information to the Redux store.
 */

export const processAssignedAlarmDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ASSIGNED_ALARM_DEVICE_DETAILS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_ASSIGNED_ALARM_DEVICE_DETAILS,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
      store.dispatch(
        updateApiSpinner({ associatedAlarmDevicesReturned: true })
      );
    }

    const initialArray = action.payload.results;

    const alarmDetails = {};
    initialArray.map((device) => {
      alarmDetails[device.alarm_device_id] = {
        alarm_device_id: device.alarm_device_id,
        alarm_description: device.alarm_description,
        unit: device.unit,
        user: device.user,
        alarm_device_type: device.alarm_device_type,
        hardware_type: device.hardware_type,
      };
      return '';
    });

    store.dispatch(addAlarmDeviceDetails(alarmDetails));
  }
};

/**
 * Load details for a single alarm device
 */
export const loadAlarmDeviceDetail = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ALARM_DEVICE_DETAILS) {
    const body = store.getState().auth.token;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alarmdevice +
        action.payload +
        '/',
      onSuccess: PROCESS_ALARM_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Load alarm device types
 */
export const loadAlarmDeviceTypes = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ALARM_DEVICE_TYPES) {
    const body = store.getState().auth.token;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alarmdevicetype,
      onSuccess: PROCESS_ALARM_DEVICE_TYPES,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process the alarm device details
 *
 * Format the results to get rid of a lot of the unused information to use less space
 * in storage, and then dispatch the information to the Redux store.
 */

export const processAlarmDeviceDetail = ({ dispatch }) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ALARM_DEVICE_DETAILS) {
    dispatch(hideLoading());
    const alarmDetail = {
      [action.payload.alarm_device_id]: {
        alarm_device_id: action.payload.alarm_device_id,
        alarm_description: action.payload.alarm_description,
        unit: action.payload.unit,
        user: action.payload.user,
        alarm_device_type: action.payload.alarm_device_type,
        hardware_type: action.payload.hardware_type,
      },
    };
    dispatch(addAlarmDeviceDetails(alarmDetail));
  }
};

/**
 * Display an error when an error occurs
 */
export const deviceError = (store) => (next) => (action) => {
  next(action);
  if (action.type === DEVICE_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="alarmDeviceMiddleware.deviceErrorText"
          defaultMessage="There was an error"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Load alarm devices details for unassigned devices
 *
 * Alarmdevice endpoint with filters for monitored=true, assigned=false, and is_poi=false
 */
export const loadUnassignedAlarmDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === LOAD_UNASSIGNED_ALARM_DEVICE_DETAILS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alarmdevice +
        '?monitored=true&assigned=false&is_poi=false',
      onSuccess: PROCESS_UNASSIGNED_ALARM_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ alarmDevicesReturned: false }));
  }
};

/**
 * Load alarm devices details for unassigned poi devices
 *
 * Alarmdevice endpoint with filters for monitored=true, assigned=false, is_poi=true, ignore_sps_locations=0
 */

export const loadUnassignedPoiDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === LOAD_UNASSIGNED_POI_DEVICE_DETAILS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alarmdevice +
        '?monitored=true&assigned=false&is_poi=true&ignore_sps_locations=0',
      onSuccess: PROCESS_UNASSIGNED_ALARM_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ alarmDevicesReturned: false }));
  }
};

/**
 * Process returned alarm device details
 *
 * If there are paged results, fire off api call for next item
 * If no further pages, hide the loading bar and update the spinner
 */

export const processUnassignedAlarmDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_UNASSIGNED_ALARM_DEVICE_DETAILS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_UNASSIGNED_ALARM_DEVICE_DETAILS,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
      store.dispatch(updateApiSpinner({ alarmDevicesReturned: true }));
    }

    const initialArray = action.payload.results;
    const alarmDetails = {};
    initialArray.map((device) => {
      alarmDetails[device.alarm_device_id] = {
        alarm_device_id: device.alarm_device_id,
        alarm_description: device.alarm_description,
        unit: device.unit,
        user: device.user,
        alarm_device_type: device.alarm_device_type,
        hardware_type: device.hardware_type,
      };
      return '';
    });

    store.dispatch(addAlarmDeviceDetails(alarmDetails));
  }
};

/**
 * Process alarm device types
 *
 * The data returns as an array and is put into an object with the key as the alarm device type id. That object is then added to the Redux store.
 */

export const processAlarmDeviceTypes = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ALARM_DEVICE_TYPES) {
    store.dispatch(hideLoading());

    const initialArray = action.payload.results;
    const types = {};
    initialArray.map((device) => {
      types[device.alarm_device_type] = { ...device };
      return '';
    });

    store.dispatch(addAlarmDeviceTypes(types));
  }
};

export const loadSpecificAlarmDevicesDetails = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_SPECIFIC_ALARM_DEVICE_DETAILS) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + 
           apiVersion + 
           apiUrls[apiVersion].alarmdevice +
           '?fields=alarm_device_id,alarm_device_type,alarm_description,serial_id&limit=100000',
      onSuccess: PROCESS_SPECIFIC_ALARM_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ specificAlarmDevicesReturned: false }));
  }
};

export const processSpecificAlarmDevicesDetails = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_SPECIFIC_ALARM_DEVICE_DETAILS) {
    
    
    store.dispatch(hideLoading());
    store.dispatch(updateApiSpinner({ specificAlarmDevicesReturned: true }));

    const initialArray = action.payload.results;
    const alarmDetails = {};
    initialArray.map((device) => {
      alarmDetails[device.alarm_device_id] = {
        alarm_device_id: device.alarm_device_id,
        alarm_description: device.alarm_description,
        alarm_device_type: device.alarm_device_type,
        serial_id: device.serial_id,
      };
      return '';
    });

   
    store.dispatch(addSpecificAlarmDeviceDetails(alarmDetails));
  }
};

export const alarmDevicesMdl = [
  loadAssignedAlarmDevicesDetails,
  processAssignedAlarmDevicesDetails,
  deviceError,
  loadAlarmDeviceDetail,
  processAlarmDeviceDetail,
  loadUnassignedAlarmDevicesDetails,
  processUnassignedAlarmDevicesDetails,
  loadAlarmDeviceTypes,
  processAlarmDeviceTypes,
  loadUnassignedPoiDevicesDetails,
  loadSpecificAlarmDevicesDetails,
  processSpecificAlarmDevicesDetails,
];
